import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from '@/services/axios';

import { initialState } from '../constants';

/**
 * A custom hook to predict the initial region for a new PBX registrar with a single region
 * @param {{id: string, name: string, [x: string]: any}[]} regions
 * @param {Boolean} isGlobalAdmin
 * */
function useInitialRegion(regions, isGlobalAdmin) {
    const { requiredScope } = useSelector((state) => state.login);
    const [trsClusters, setTrsClusters] = useState([]);
    const [trsClustersLoading, setTrsClustersLoading] = useState(false);
    const [predictedInitialState, setPredictedInitialState] =
        useState(initialState);

    const trsQuery = useQuery({
        queryKey: ['TRSClusters'],
        queryFn: async () => {
            const res = await axios.get('/TRSClusters', {
                headers: {
                    'X-RequestScope': requiredScope,
                },
            });
            if (res.status !== 200) {
                throw new Error(
                    res.data?.message || 'Error fetching TRS clusters',
                );
            }
            return res.data;
        },
        onSuccess: (data) => setTrsClusters(data || []),
        retry: 2,
        refetchOnWindowFocus: false,
        enabled: isGlobalAdmin && regions.length === 1,
    });

    useEffect(() => {
        if (isGlobalAdmin && trsQuery.isLoading) {
            setTrsClustersLoading(true);
        } else if (isGlobalAdmin && !trsQuery.isLoading) {
            setTrsClustersLoading(false);
        }
    }, [isGlobalAdmin, trsQuery.isLoading]);

    // Region prediction logic for Global Admins
    useEffect(() => {
        if (
            isGlobalAdmin &&
            trsClusters.length > 0 &&
            !trsClustersLoading &&
            regions.length > 0
        ) {
            // Extract unique regions from TRS clusters
            const uniqueRegions = [
                ...new Set(
                    trsClusters
                        .map((cluster) => cluster.region?.name)
                        .filter(Boolean),
                ),
            ];

            // Only auto-select if there's exactly one unique region
            if (uniqueRegions.length === 1) {
                const trsRegionName = uniqueRegions[0];

                // Map TRS cluster region name to available region ID
                const matchingRegion = regions.find(
                    (region) =>
                        region.name?.toLowerCase() ===
                            trsRegionName?.toLowerCase() ||
                        region.name
                            ?.toLowerCase()
                            .includes(trsRegionName?.toLowerCase()) ||
                        trsRegionName
                            ?.toLowerCase()
                            .includes(region.name?.toLowerCase()),
                );

                if (
                    matchingRegion &&
                    predictedInitialState.regionID !== matchingRegion.id
                ) {
                    setPredictedInitialState((prev) => ({
                        ...prev,
                        regionID: matchingRegion.id,
                    }));
                }
            }
        }
    }, [
        isGlobalAdmin,
        trsClusters,
        trsClustersLoading,
        regions,
        predictedInitialState.regionID,
    ]);

    return {
        trsClusters,
        trsClustersLoading,
        predictedInitialState,
    };
}

export { useInitialRegion };
